import { reshapeVariantForCart } from '@/components/blocks/cross_sell/CrossSellContents';

/**
 * Keeps the selected cross-sell add-ons in sync with the primary product's
 * options.
 * @param optionName - The name of the option that is being changed (e.g. Size, Color, Material)
 * @param optionValue - The specific variant option selected (e.g. White, Low Loft, Queen)
 * @param additionalProducts - The currently selected cross-sell variants from product context
 * @param crossSellProducts - All cross sell products that are offered in the buy box, from product context
 */
function determineAdditionalProducts(
  optionName,
  optionValue,
  additionalProducts,
  crossSellProducts
) {
  const lowercaseOptionName = optionName.toLowerCase();

  const newAdditionalProducts = [];

  additionalProducts?.forEach((additionalProduct) => {
    const matchingProduct = crossSellProducts.find(
      (product) => product.id == additionalProduct.productId
    );
    let matchingVariant = null;
    // if size changes, clear all additional products unless they are single size
    if (lowercaseOptionName == 'size') {
      if (matchingProduct?.singleSize) {
        matchingVariant = matchingProduct?.variants.find(
          (variant) => variant.id == additionalProduct.id
        );
      } else {
        matchingVariant = null;
      }
    }
    // if single size item, try to find a non-size matching option value
    else if (matchingProduct?.singleSize) {
      matchingVariant = matchingProduct?.variants.find((variant) => {
        const valueToMatch = variant?.selectedOptions?.find(
          (option) => option?.name.toLowerCase() === lowercaseOptionName
        )?.value;
        return valueToMatch === optionValue;
      });
    }
    // if there is no secondary option, or if we are overriding it, keep the current variant
    else if (
      matchingProduct?.secondaryAttrOverride ||
      (matchingProduct.options.length == 1 &&
        matchingProduct.options[0].name == 'Size')
    ) {
      matchingVariant = matchingProduct?.variants.find(
        (variant) => variant.id == additionalProduct.id
      );
    }
    // otherwise we need to match size and secondary attribute
    else {
      matchingVariant = matchingProduct?.variants.find((variant) => {
        const sizeToMatch = variant?.selectedOptions?.find(
          (option) => option?.name === 'Size'
        )?.value;
        const secondaryToMatch = variant?.selectedOptions?.find(
          (option) => option?.name.toLowerCase() === lowercaseOptionName
        )?.value;

        return (
          secondaryToMatch === optionValue &&
          sizeToMatch ===
            additionalProduct?.selectedOptions?.find(
              (option) => option.name == 'Size'
            )?.value
        );
      });
    }

    if (matchingVariant) {
      matchingVariant = {
        ...matchingVariant,
        productTitle: matchingProduct?.title,
        productHandle: matchingProduct?.handle,
        productImage: matchingVariant?.image?.url,
        productImageTitle: matchingProduct?.title,
        productId: matchingProduct?.id,
        siblingProductTitle: '',
        type: matchingProduct?.productType,
        outOfStockCopy: 'Out of Stock'
      };

      const variantObj = reshapeVariantForCart(
        matchingVariant,
        matchingProduct
      );

      if (matchingVariant?.outOfStock?.value == 'false') {
        newAdditionalProducts.push(variantObj);
      }
    }
  });
  return newAdditionalProducts;
}

/**
 * Remaps the selected add-ons for an options change, returning the array the
 * selection should become.
 *
 * `changedOptions` is whatever was handed to `updateOption`, which is sometimes
 * a single option (`{ size: 'King' }`) and sometimes a product's full set of
 * selected options. Only the options whose value actually differs from
 * `previousOptions` are remapped, so passing the full set doesn't re-run the
 * match for options the shopper didn't touch.
 *
 * Returns `additionalProducts` untouched when there's nothing to remap.
 */
export function remapAdditionalProducts({
  changedOptions,
  previousOptions,
  additionalProducts,
  crossSellProducts
}) {
  // No add-ons selected, or nothing to match them against. The latter shouldn't
  // happen (you can't select an add-on that isn't offered) but bailing beats
  // clearing a selection we can't reason about.
  if (!additionalProducts?.length || !crossSellProducts?.length) {
    return additionalProducts;
  }

  return Object.entries(changedOptions).reduce(
    (remapped, [optionName, optionValue]) =>
      previousOptions?.[optionName] === optionValue
        ? remapped
        : determineAdditionalProducts(
            optionName,
            optionValue,
            remapped,
            crossSellProducts
          ),
    additionalProducts
  );
}
